import { isEmptyObject } from '@teko/utils';
import {
  CONSENT_NAME,
  CONSENT_VALUES,
  COOKIE_CONSENT_COOKIE,
  COOKIE_CONSENT_DURATION,
} from 'constants/index';
import {
  getCookieConsentFromBrowserCookies,
  getExpireTime,
  isConsentApproved,
  isServerless,
  setCookieByName,
} from 'helpers';
import { GtagConsentParams } from 'libs';
import { Consent, CookieConsent } from 'types';
import { StoreApi, create } from 'zustand';

interface CookieConsentStore {
  visible: boolean;
  setVisible: (value: boolean) => void;
  cookieConsent?: CookieConsent;
  setCookieConsent: (consents: Consent[]) => void;
  // Re-apply stored consent to GA/GTM on every full page load,
  // since consent mode resets to `default` (denied) each time.
  applyConsentFromCookie: () => void;
  submitConsentToServices: (consents: Consent[]) => void;
  adStorage: () => boolean;
  adUserData: () => boolean;
  adPersonalization: () => boolean;
  analyticsStorage: () => boolean;
  functionalityStorage: () => boolean;
  personalizationStorage: () => boolean;
  securityStorage: () => boolean;
}

// Set cookie consent values
const setCookieConsentToBrowserCookies = (
  set: StoreApi<CookieConsentStore>['setState'],
  consents: Consent[],
) => {
  const expiredAt = getExpireTime(COOKIE_CONSENT_DURATION);

  // Update consent values in store
  set({
    cookieConsent: {
      consents,
      expiredAt,
    },
  });

  // Upsert cookie consent values to `cookie_consent` in browser cookies
  setCookieByName(
    COOKIE_CONSENT_COOKIE,
    JSON.stringify({
      consents,
      expiredAt,
    }),
    expiredAt,
  );
};

// Submit cookie consent to GA/GTM via consent mode `update`.
// Only the categories sent here are changed; others keep their previous value.
const submitGAGTM = (consents: Consent[]) => {
  if (!isServerless() || !window.gtag) return;
  if (!!consents?.length) {
    const consentValues: GtagConsentParams = {};
    consents.forEach((consent) => {
      consent.policyValues?.forEach((policyValue) => {
        Object.assign(consentValues, {
          [policyValue]: !!consent.isApproved
            ? CONSENT_VALUES.GRANTED
            : CONSENT_VALUES.DENIED,
        });
      });
    });
    window.gtag('consent', 'update', consentValues);
  }
};

// Ref: https://developers.facebook.com/docs/meta-pixel/get-started/
const submitFacebookPixel = (consents: Consent[]) => {
  if (!isServerless() || !window.fbq || !consents?.length) return;
  const granted =
    isConsentApproved(CONSENT_NAME.AD_STORAGE, consents) &&
    isConsentApproved(CONSENT_NAME.AD_USER_DATA, consents) &&
    isConsentApproved(CONSENT_NAME.AD_PERSONALIZATION, consents);
  window.fbq('consent', granted ? CONSENT_VALUES.GRANT : CONSENT_VALUES.REVOKE);
};

// Submit cookie consent to Microsoft Clarity via the Consent API v2
// Ref: https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-consent-api-v2
const submitClarity = (consents: Consent[]) => {
  if (!isServerless() || !window.clarity || !consents?.length) return;
  window.clarity('consentv2', {
    ad_Storage: isConsentApproved(CONSENT_NAME.AD_STORAGE, consents)
      ? CONSENT_VALUES.GRANTED
      : CONSENT_VALUES.DENIED,
    analytics_Storage: isConsentApproved(
      CONSENT_NAME.ANALYTICS_STORAGE,
      consents,
    )
      ? CONSENT_VALUES.GRANTED
      : CONSENT_VALUES.DENIED,
  });
};

const submitConsentToServices = (consents: Consent[]) => {
  // Reflect the user's choice to GA/GTM
  submitGAGTM(consents);
  // Reflect the user's choice to 3rd parties scripts
  submitFacebookPixel(consents);
  submitClarity(consents);
};

const getConsent = (
  get: StoreApi<CookieConsentStore>['getState'],
  consentName: CONSENT_NAME,
): boolean => {
  const cookieConsent =
    get()?.cookieConsent ?? getCookieConsentFromBrowserCookies();
  if (!isEmptyObject(cookieConsent)) {
    return isConsentApproved(consentName, cookieConsent.consents);
  }
  return false;
};

export const useCookieConsentStore = create<CookieConsentStore>()(
  (set, get) => {
    const initialCookieConsent = getCookieConsentFromBrowserCookies();
    return {
      visible: isEmptyObject(initialCookieConsent),
      setVisible: (visible) => set({ visible }),
      cookieConsent: initialCookieConsent,
      setCookieConsent: (consents) => {
        setCookieConsentToBrowserCookies(set, consents);
      },
      applyConsentFromCookie: () => {
        const cookieConsent = get()?.cookieConsent ?? initialCookieConsent;
        if (!isEmptyObject(cookieConsent)) {
          submitConsentToServices(cookieConsent.consents);
        }
      },
      submitConsentToServices,
      adStorage: () => getConsent(get, CONSENT_NAME.AD_STORAGE),
      adUserData: () => getConsent(get, CONSENT_NAME.AD_USER_DATA),
      adPersonalization: () => getConsent(get, CONSENT_NAME.AD_PERSONALIZATION),
      analyticsStorage: () => getConsent(get, CONSENT_NAME.ANALYTICS_STORAGE),
      functionalityStorage: () =>
        getConsent(get, CONSENT_NAME.FUNCTIONALITY_STORAGE),
      personalizationStorage: () =>
        getConsent(get, CONSENT_NAME.PERSONALIZATION_STORAGE),
      securityStorage: () => getConsent(get, CONSENT_NAME.SECURITY_STORAGE),
    };
  },
);
